import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EventEmitter } from "@angular/core";
import { Storage } from '@ionic/storage';
import jwt_decode from "jwt-decode";

import { AppConfig } from '@app/config'
import {
  AcceptInvitationData, DecodedToken, LoginData, ResetPasswordData, TokenResponse,
  User
} from "../app/app.interfaces";


@Injectable()
export class AuthService {

  private readonly getTokenPath: string = '/jwt/token';
  private readonly refreshTokenPath: string = '/jwt/token/refresh';
  private readonly requestPasswordResetPath: string = '/user/password-reset/request';
  private readonly requestUserRegistrationPath: string = '/user/registration/request';
  private readonly validatePasswordResetTokenPath: string = '/user/password-reset/validate-token';
  private readonly validateUserInviteTokenPath: string = '/user/invite/validate-token';
  private readonly acceptInvitationPath: string = '/user/invite/accept';
  private readonly setNewPasswordPath: string = '/user/password-reset/set-new-password';

  public userLogout: EventEmitter<User> = new EventEmitter();
  public beforeUserLogout: EventEmitter<User> = new EventEmitter();
  public userLogin: EventEmitter<User> = new EventEmitter();
  public initialized: EventEmitter<void> = new EventEmitter();
      
  private tokenData: TokenResponse = null;
  private decodedToken: DecodedToken = null;
  
  private tokenPromise: Promise<string> = null;
  
  private _initialized: boolean = false;
    
  constructor(private http: HttpClient, private storage: Storage) {
  }
    
  public initialize(): void {
        
    if(this.tokenData !== null){
      this.initialized.emit();
    }
    else {
      this.storage.get('tokenData').then((tokenData: any)=>{
        //Always refresh token from storage, to check if it's still okay
        if(tokenData){
          this.tokenData = tokenData;
          this.renewToken().then(() => {
            this.initialized.emit();
          }).catch((err)=>{
            this.clearTokenData();
            this.initialized.emit(); 
          });          
        }
        else {
          this.clearTokenData();
          this.initialized.emit();          
        }
        
      }).catch((err) => {
        this.clearTokenData();
        this.initialized.emit();
      });
    }
    
  }
  
  public afterInit(): Promise<void> {    
    return new Promise((resolve) => {
      if (this._initialized) {
        resolve();
      }
      else {
        this.initialized.subscribe(() => {
          this._initialized = true;
          resolve();
        });
      }
    });
  }
  
  public afterTokenLoad(): Promise<void> {
    return new Promise((resolve) => {      
      this.afterInit().then(() => {
        if(this.tokenData !== null) {
          resolve();
        }
      });      
    });
  }
  
  
  public isLoggedIn(): Promise<boolean> {    
    return new Promise((resolve)=>{
      this.afterInit().then(()=>{  
        resolve((this.tokenData !== null));
      });
    }); 
  }
  
  public logout(): void {   
    this.userLogout.emit(this.user);
    this.clearTokenData();
  }
  
  private clearTokenData(): void {
    this.tokenData = null;
    this.decodedToken = null;
    this.storage.remove('tokenData');    
  }
      
  get user(): User {
    if(! this.decodedToken || ! this.decodedToken.user) return null;
    return this.decodedToken.user;
  }
  
  public setUserData(key, value): void{
    if(! this.decodedToken) return;
    if(! this.decodedToken.user) return;  
    
    this.decodedToken.user[key] = value;
  }

  public setUser(user: User): void{
    this.decodedToken.user = user;
  }
  
    
  get token(): string {
    if(! this.tokenData) return null;
    if(! this.tokenData.token) return null;   
    return this.tokenData.token;
  }


  public login(loginData: LoginData): Promise<void> {
    
    return new Promise((resolve, reject) => {
      const getTokenUrl = AppConfig.apiBaseUrl + this.getTokenPath;      
      this.http.post(getTokenUrl, { email: loginData.email, password:  loginData.password })
        .toPromise()
        .then((tokenData: TokenResponse) => {
          this.handleTokenResponse(tokenData, loginData.rememberLogin).then(()=>{
            resolve();      
          }, ()=>{
            reject();
          });
        }, err => {
            reject();      
        });
    });

  }
  
  
  public renewToken(): Promise<string> {
    
    if(this.tokenPromise){
      return this.tokenPromise;
    }
    
    this.tokenPromise = new Promise((resolve, reject) => {
      
      if(this.tokenData === null) reject();
      if(this.tokenData.refresh_token === null) reject();
      
      const refreshTokenUrl = AppConfig.apiBaseUrl + this.refreshTokenPath;
      this.http.post(refreshTokenUrl, { refresh_token: this.tokenData.refresh_token })
        .toPromise()
        .then((tokenData: TokenResponse) => {        
          this.handleTokenResponse(tokenData, true).then(()=>{
            this.tokenPromise = null;
            resolve();      
          }, () => {
            this.tokenPromise = null;
            this.clearTokenData();
            reject();
          });
        }, () => {
          this.tokenPromise = null;
          this.clearTokenData();
          reject();
        });
      
    });
    
    return this.tokenPromise;
    
  }
  
  
  public requestPasswordReset(email: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const passwordResetUrl = AppConfig.apiBaseUrl + this.requestPasswordResetPath;      
      this.http.post(passwordResetUrl, { email: email })
        .toPromise()
        .then((success) => {
          resolve(success);
        }, (error) => {
          reject(error.error);
        });      
    });
  }
  public validatePasswordResetToken(token: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const tokenValidationUrl = AppConfig.apiBaseUrl + this.validatePasswordResetTokenPath;      
      this.http.post(tokenValidationUrl, { token: token })
        .toPromise()
        .then((data) => {
          resolve(data);
        }, (error) => {
          reject(error.error);
        });      
    });
  }
  
  public setNewPassword(data: ResetPasswordData): Promise<any> {
    return new Promise((resolve, reject) => {
      const setNewPasswordUrl = AppConfig.apiBaseUrl + this.setNewPasswordPath;      
      this.http.post(setNewPasswordUrl, data)
        .toPromise()
        .then((data) => {
          resolve(data);
        }, (error) => {
          reject(error.error);
        });      
    });
  }

  public acceptInvitation(data: AcceptInvitationData): Promise<any> {
    return new Promise((resolve, reject) => {
      const acceptInvitationUrl = AppConfig.apiBaseUrl + this.acceptInvitationPath;      
      this.http.post(acceptInvitationUrl, data)
        .toPromise()
        .then((data) => {
          resolve(data);
        }, (error) => {
          reject(error.error);
        });      
    });
  }
  
  public validateUserInviteToken(token: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const tokenValidationUrl = AppConfig.apiBaseUrl + this.validateUserInviteTokenPath;      
      this.http.post(tokenValidationUrl, { token: token })
        .toPromise()
        .then((data) => {
          resolve(data);
        }, (error) => {
          reject(error.error);
        });      
    });
  }
  
  
  public requestUserRegistration(email: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const passwordResetUrl = AppConfig.apiBaseUrl + this.requestUserRegistrationPath;      
      this.http.post(passwordResetUrl, { email: email })
        .toPromise()
        .then((success) => {
          resolve(success);
        }, (error) => {
          reject(error.error);
        });      
    });
  }
  
  private handleTokenResponse(tokenData: TokenResponse, saveToStorage: boolean = false): Promise<void> {
    
    return new Promise((resolve, reject) => {
      if(tokenData !== null) {            
        this.tokenData = tokenData;        
        this.decodedToken = jwt_decode(tokenData.token);  
        resolve();
        if (saveToStorage)  this.storage.set('tokenData', tokenData);
        this.userLogin.emit(this.user);
      }
      else {
        reject();
      }
      
    });
    
  }
  
}


// WEBPACK FOOTER //
// ./src/services/auth.service.ts